'use client';

import { useTheme } from '@/contexts/ThemeContext';
import styles from './HowToPlay.module.css';
import { useTranslation } from 'next-i18next';

export interface HowToPlayStep {
  number: string;
  title: string;
  description: string;
  backgroundImage: string;
  powerHourText?: string;
}

interface HowToPlayProps {
  steps?: HowToPlayStep[];
  showDots?: boolean;
  activeDotIndex?: number;
}

const defaultSteps: HowToPlayStep[] = [
  {
    number: '#1',
    title: 'HOW TO PLAY',
    description:
      'Pick the hole you are watching from to see what group is up next.',
    backgroundImage: '/howtoplay/1.png',
    powerHourText: 'Correct votes earn double points in Power Hour!',
  },
  {
    number: '#2',
    title: 'PREDICT THE SCORES',
    description:
      'Predict the results of the players in the upcoming group: Birdie (or better), Par, or Bogie (or worse).',
    backgroundImage: '/howtoplay/2.png',
  },
  {
    number: '#3',
    title: 'EARN POINTS',
    description:
      'Get 1 point for each correct score prediction, then check your position on the overall leaderboard for a chance to win some amazing rewards.',
    backgroundImage: '/howtoplay/3.png',
  },
];

export default function HowToPlay({
  steps = defaultSteps,
  showDots = false,
  activeDotIndex = 0,
}: HowToPlayProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();

  return (
    <div className={styles.howToPlaySection}>
      <div className={styles.howToPlayCards}>
        {steps.map((step, index) => (
          <div key={index} className={styles.howToPlayCard}>
            <div
              className={styles.cardBackground}
              style={{ backgroundImage: `url(${step.backgroundImage})` }}
            ></div>
            <div className={styles.cardHeader}>
              <div
                className={
                  theme === 'houston' || theme === 'shenzhen'
                    ? styles.cardNumberHouston
                    : styles.cardNumber
                }
              >
                {step.number}
              </div>
              <h2 className={styles.cardTitle}>
                {t(step.title)
                  .split('\n')
                  .map((line, lineIndex) => (
                    <span key={lineIndex}>
                      {t(line)}
                      {lineIndex < step.title.split('\n').length - 1 && <br />}
                    </span>
                  ))}
              </h2>
            </div>
            <p className={styles.cardText}>{t(step.description)}</p>
            {theme === 'houston' ||
              (theme === 'shenzhen' && step.powerHourText && (
                <p className={styles.powerHourText}>{t(step.powerHourText)}</p>
              ))}
          </div>
        ))}
      </div>

      {showDots && (
        <div className={styles.dots}>
          {steps.map((_, index) => (
            <div
              key={index}
              className={`${styles.dot} ${index === activeDotIndex ? styles.active : ''}`}
            ></div>
          ))}
        </div>
      )}
    </div>
  );
}
